import { useEffect, useState, useCallback } from "react";
import { CalendarCheck, Plus, Phone, Clock, Trash2, User, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const statusColor = {
  confirmed: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700",
};

const PAY_STYLES = {
  paid: "bg-emerald-100 text-emerald-700",
  deposit_paid: "bg-teal-100 text-teal-700",
  pending: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
  refunded: "bg-slate-200 text-slate-600",
  expired: "bg-orange-100 text-orange-700",
};
const PAY_LABEL = {
  paid: "Paid", deposit_paid: "Deposit paid", pending: "Payment pending",
  failed: "Payment failed", refunded: "Refunded", expired: "Payment expired",
};

export default function Appointments() {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ customer_name: "", phone: "", service: "", staff_id: "", date: "", time: "", notes: "" });
  const [filters, setFilters] = useState({ staff: "all", service: "all", status: "all", date: "" });

  const load = useCallback(() => api.get("/appointments").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/staff").then((r) => setStaff(r.data.staff || [])).catch(() => {});
    api.get("/business").then((r) => setServices((r.data.service_list || []).filter((s) => s.enabled !== false && s.name))).catch(() => {});
  }, [load]);

  // Real-time: refresh bookings when a payment webhook confirms/cancels one (no page refresh needed).
  useEffect(() => {
    window.addEventListener("payment-updated", load);
    return () => window.removeEventListener("payment-updated", load);
  }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (!form.date || !form.time) return toast.error("Please choose a date and time");
    setSaving(true);
    try {
      await api.post("/appointments/book", {
        customer_name: form.customer_name, phone: form.phone, service: form.service,
        date: form.date, time: form.time, staff_id: form.staff_id === "any" ? "" : form.staff_id, notes: form.notes,
      });
      toast.success("Appointment booked");
      setForm({ customer_name: "", phone: "", service: "", staff_id: "", date: "", time: "", notes: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not book appointment");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/appointments/${id}`);
    load();
  };

  const cycleStatus = async (a) => {
    const order = ["confirmed", "pending", "cancelled"];
    const next = order[(order.indexOf(a.status) + 1) % order.length];
    await api.patch(`/appointments/${a.id}`, { status: next });
    load();
  };

  const serviceOptions = Array.from(new Set(items.map((a) => a.service).filter(Boolean)));
  const filtered = items.filter((a) => {
    if (filters.staff !== "all" && (a.staff_id || "") !== filters.staff) return false;
    if (filters.service !== "all" && a.service !== filters.service) return false;
    if (filters.status !== "all" && a.status !== filters.status) return false;
    if (filters.date && (a.start || "").slice(0, 10) !== filters.date) return false;
    return true;
  });

  return (
    <div data-testid="appointments-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Appointments</h1>
          <p className="text-slate-500 mt-1">Bookings captured by your AI receptionist — and added manually.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-appointment-btn" className="bg-blue-600 hover:bg-blue-700 gap-2"><Plus className="h-4 w-4" /> Add</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">New appointment</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Customer name *</Label>
                <Input data-testid="appt-customer_name" value={form.customer_name} onChange={(e) => setF("customer_name", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input data-testid="appt-phone" value={form.phone} onChange={(e) => setF("phone", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Service</Label>
                {services.length > 0 ? (
                  <Select value={form.service} onValueChange={(v) => setF("service", v)}>
                    <SelectTrigger data-testid="appt-service" className="mt-1"><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent>{services.map((s) => <SelectItem key={s.id || s.name} value={s.name}>{s.name}{s.duration ? ` · ${s.duration} min` : ""}{s.price ? ` · ${s.price}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Input data-testid="appt-service" value={form.service} onChange={(e) => setF("service", e.target.value)} className="mt-1" placeholder="Service" />}
              </div>
              {staff.length > 0 && (
                <div>
                  <Label className="text-xs">Staff member</Label>
                  <Select value={form.staff_id || "any"} onValueChange={(v) => setF("staff_id", v)}>
                    <SelectTrigger data-testid="appt-staff" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="any">Any available</SelectItem>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Date *</Label><Input data-testid="appt-date" type="date" value={form.date} onChange={(e) => setF("date", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Time *</Label><Input data-testid="appt-time" type="time" value={form.time} onChange={(e) => setF("time", e.target.value)} className="mt-1" /></div>
              </div>
              <div><Label className="text-xs">Notes</Label><Input data-testid="appt-notes" value={form.notes} onChange={(e) => setF("notes", e.target.value)} className="mt-1" /></div>
              <p className="text-xs text-slate-400">We'll check availability and prevent double-booking automatically.</p>
            </div>
            <DialogFooter>
              <Button data-testid="save-appointment-btn" onClick={create} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? "Booking…" : "Book appointment"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div data-testid="appt-filters" className="mt-5 flex flex-wrap items-center gap-2">
        {staff.length > 0 && (
          <Select value={filters.staff} onValueChange={(v) => setFilters({ ...filters, staff: v })}>
            <SelectTrigger data-testid="filter-staff" className="h-9 w-44"><SelectValue placeholder="Staff" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All staff</SelectItem>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={filters.service} onValueChange={(v) => setFilters({ ...filters, service: v })}>
          <SelectTrigger data-testid="filter-service" className="h-9 w-44"><SelectValue placeholder="Service" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All services</SelectItem>{serviceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger data-testid="filter-status" className="h-9 w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>{["all", "confirmed", "pending", "cancelled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="filter-date" type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} className="h-9 w-40" />
        {(filters.staff !== "all" || filters.service !== "all" || filters.status !== "all" || filters.date) &&
          <Button variant="ghost" size="sm" onClick={() => setFilters({ staff: "all", service: "all", status: "all", date: "" })} className="text-slate-500 h-9">Clear</Button>}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        {filtered.length === 0 && <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-400 text-sm sm:col-span-2">No appointments match your filters.</div>}
        {filtered.map((a) => (
          <div key={a.id} data-testid="appointment-card" className="rounded-xl border border-slate-200 bg-white p-5 transition-all hover:shadow-md">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600"><CalendarCheck className="h-5 w-5" /></div>
                <div>
                  <p className="font-semibold text-slate-900">{a.customer_name}</p>
                  <p className="text-sm text-slate-500">{a.service || "—"}</p>
                </div>
              </div>
              <button data-testid="appt-status" onClick={() => cycleStatus(a)} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${statusColor[a.status] || statusColor.pending}`}>{a.status === "pending_payment" ? "awaiting payment" : a.status}</button>
            </div>
            {a.payment_status && a.payment_status !== "none" && (
              <div className="mt-2">
                <span data-testid="appt-payment-status" className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${PAY_STYLES[a.payment_status] || "bg-slate-100 text-slate-600"}`}>
                  <CreditCard className="h-3 w-3" /> {PAY_LABEL[a.payment_status] || a.payment_status}
                </span>
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-600">
              {a.when && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" /> {a.when}</span>}
              {a.staff_name && <span data-testid="appt-staff" className="flex items-center gap-1.5 text-blue-700 font-medium"><User className="h-4 w-4 text-blue-400" /> {a.staff_name}</span>}
              {a.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-slate-400" /> {a.phone}</span>}
            </div>
            {a.notes && <p className="mt-2 text-sm text-slate-500">{a.notes}</p>}
            <button onClick={() => remove(a.id)} className="mt-3 text-xs text-slate-400 hover:text-red-500 flex items-center gap-1 transition-colors"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}
